
import React from 'react';
import { useAuth } from '@/context/auth/AuthContext';
import { useForm } from '@/context/FormContext';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { format } from 'date-fns';
import { useToast } from '@/hooks/use-toast';
import { usePreviousReports } from './usePreviousReports';
import { PreviousReportContent } from './PreviousReportContent';

interface PreviousReportModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const PreviousReportModal: React.FC<PreviousReportModalProps> = ({
  open,
  onOpenChange,
}) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const { setField } = useForm();
  
  const {
    reports,
    loading,
    error,
    togglePinReport,
  } = usePreviousReports(open, user?.email);
  
  const handleSelectReport = (reportId: string) => {
    if (!reports) return;
    
    const selectedReport = reports.find(report => report.id === reportId);
    if (!selectedReport) return;
    
    // Pre-fill form fields with data from the selected report
    const fieldsToFill = [
      { name: 'teaching_location', value: selectedReport.teaching_location || '' },
      { name: 'teaching_type', value: selectedReport.teaching_type || '' },
      { name: 'teaching_time_slot', value: selectedReport.teaching_time_slot || '' },
      { name: 'teaching_duration', value: selectedReport.teaching_duration || '' },
      { name: 'student_full_name', value: selectedReport.student_full_name || '' },
      { name: 'student_nickname', value: selectedReport.student_nickname || '' },
      { name: 'student_id', value: selectedReport.student_id || '' },
      { name: 'student_grade_level', value: selectedReport.student_grade_level || '' },
      { name: 'subject', value: selectedReport.subject || '' },
    ];
    
    // Update form fields
    fieldsToFill.forEach(field => {
      if (field.value) {
        setField(field.name, field.value);
      }
    });
    
    toast({
      title: "ข้อมูลถูกเพิ่มเรียบร้อย",
      description: `นำข้อมูลจากรายงานวันที่ ${format(new Date(selectedReport.teaching_date), 'dd/MM/yyyy')} มาใช้แล้ว`,
      duration: 3000,
    });
    
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>เลือกข้อมูลจากรายงานล่าสุด</DialogTitle>
          <DialogDescription>
            เลือกรายงานที่ต้องการนำข้อมูลมาใช้ในฟอร์มปัจจุบัน
          </DialogDescription>
        </DialogHeader>
        
        <PreviousReportContent
          loading={loading}
          error={error}
          reports={reports}
          onTogglePin={togglePinReport}
          onSelectReport={handleSelectReport}
        />
        
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            ปิด
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PreviousReportModal;
